/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { Loader } from "google-maps";
import { CircularProgress } from "@material-ui/core";
import { DateTime } from "luxon";
import { apiReports } from "../api/reports";
import { config } from "../core";

// Log-scaled heat colors for county fill intensity. Using a log scale spreads
// low-count counties across more distinct colors when the top county dominates.
const HEAT_COLORS = [
  { threshold: 0.00, color: "#fef9c3" }, // very light yellow
  { threshold: 0.20, color: "#fde047" }, // yellow
  { threshold: 0.40, color: "#fbbf24" }, // amber
  { threshold: 0.55, color: "#f97316" }, // orange
  { threshold: 0.70, color: "#ef4444" }, // red
  { threshold: 0.85, color: "#b91c1c" }, // dark red
  { threshold: 0.97, color: "#7f1d1d" }, // very dark red
];

function heatColor(t) {
  let color = HEAT_COLORS[0].color;
  for (let i = 1; i < HEAT_COLORS.length; i++) {
    if (t >= HEAT_COLORS[i].threshold) {
      color = HEAT_COLORS[i].color;
    } else {
      break;
    }
  }
  return color;
}

function logRatio(count, max) {
  if (count <= 0 || max <= 1) {return 0;}
  return Math.log(count) / Math.log(max);
}

function countyKey(state, county) {
  return `${state}|${county}`.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function formatNumber(n) {
  return n.toLocaleString();
}

function formatMonthLabel(fromIso, toIso, fallback) {
  if (!fromIso || !toIso) {return fallback;}
  const from = DateTime.fromISO(fromIso);
  const to = DateTime.fromISO(toIso).minus({ milliseconds: 1 });
  if (!from.isValid || !to.isValid) {return fallback;}
  if (from.month === to.month && from.year === to.year) {
    return `${from.toFormat("MMM d")}–${to.toFormat("d, yyyy")}`;
  }
  return `${from.toFormat("MMM d, yyyy")}–${to.toFormat("MMM d, yyyy")}`;
}

function TrendBadge({ label, current, previous }) {
  if (!previous || previous === 0) {
    if (current > 0) {
      return <span className="tw-ml-2 tw-text-xs tw-text-green-600" title={`new vs ${label}`}>▲ new</span>;
    }
    return null;
  }
  const change = (current - previous) / previous;
  const absPct = Math.abs(change) * 100;
  if (absPct < 20) {return null;}
  const isUp = change > 0;
  const color = isUp ? "tw-text-green-600" : "tw-text-red-600";
  const arrow = isUp ? "▲" : "▼";
  return <span className={`tw-ml-2 tw-text-xs ${color}`} title={`vs ${label}: ${isUp ? "+" : "-"}${absPct.toFixed(0)}%`}>{arrow} {absPct.toFixed(0)}%</span>;
}



const IncomingInspectionsMap = () => {
  const [google, setGoogle] = useState(null);
  const [map, setMap] = useState(null);
  const [geoJsonError, setGeoJsonError] = useState(null);
  const dataLayerRef = useRef(null);
  const mapDivRef = useRef(null);
  const allGeoJsonRef = useRef(null);
  const lastFitSignatureRef = useRef("");
  const featureMapRef = useRef(new Map());
  const [hoveredKey, setHoveredKey] = useState(null);
  const [showCoverage, setShowCoverage] = useState(true);
  const [coverageMinutes, setCoverageMinutes] = useState(60);
  const coveragePolygonsRef = useRef([]);

  const { data: response, error: apiError } = useSWR(
    "incoming-map",
    () => apiReports.getIncomingMap()
  );
  const currentData = response?.data?.result?.data || [];
  const lastMonthData = response?.data?.result?.lastMonth?.data || [];
  const lastYearData = response?.data?.result?.lastYear?.data || [];
  const ageBandsData = response?.data?.result?.ageBands || [];
  const allGeoJsonFeatures = allGeoJsonRef.current?.features || [];
  const currentFrom = response?.data?.result?.from;
  const currentTo = response?.data?.result?.to;
  const lastMonthFrom = response?.data?.result?.lastMonth?.from;
  const lastMonthTo = response?.data?.result?.lastMonth?.to;
  const lastYearFrom = response?.data?.result?.lastYear?.from;
  const lastYearTo = response?.data?.result?.lastYear?.to;
  const canonicalNames = response?.data?.result?.canonicalNames || {};
  const loading = !response && !apiError;


  const { data: newAreasData } = useSWR(
    "incoming-map-new-areas",
    () => apiReports.getNewAreas(),
    { refreshInterval: 300000 }
  );

  const { data: coverageData, isValidating: coverageLoading } = useSWR(
    showCoverage ? ["inspector-coverage", coverageMinutes] : null,
    () => apiReports.getInspectorCoverage(coverageMinutes),
    { refreshInterval: 3600000 }
  );

  const newAreaKeys = useMemo(() => {
    const set = new Set();
    (newAreasData?.data?.result || []).forEach((item) => {
      set.add(countyKey(item.state, item.county));
    });
    return set;
  }, [newAreasData]);

  const currentLabel = formatMonthLabel(currentFrom, currentTo, "Current");
  const lastMonthLabel = formatMonthLabel(lastMonthFrom, lastMonthTo, "Last Month");
  const lastYearLabel = formatMonthLabel(lastYearFrom, lastYearTo, "July 2025");

  const countyCounts = useMemo(() => {
    const map = new Map();
    currentData.forEach((item) => {
      if (item.state && item.county) {
        map.set(countyKey(item.state, item.county), item.count);
      }
    });
    return map;
  }, [currentData]);

  // Per-county open-inspection counts bucketed by age (now - created_at).
  // Keyed by countyKey -> { "0-7": n, "8-14": n, ... }.
  const countyAgeBands = useMemo(() => {
    const map = new Map();
    ageBandsData.forEach((item) => {
      if (!item.state || !item.county) {return;}
      const key = countyKey(item.state, item.county);
      const bands = map.get(key) || {};
      bands[item.ageBand] = item.count;
      map.set(key, bands);
    });
    return map;
  }, [ageBandsData]);

  const { maxCount } = useMemo(() => {
    let max = 0;
    currentData.forEach((item) => {
      if (item.count > max) {
        max = item.count;
      }
    });
    return { maxCount: max };
  }, [currentData]);

  useEffect(() => {
    let cancelled = false;
    const loader = new Loader(config.mapsKeyOpenMap);
    loader.load().then((g) => {
      if (cancelled) {return;}
      setGoogle(g);
      const m = new g.maps.Map(mapDivRef.current, {
        center: { lat: 32, lng: -85 },
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
        scaleControl: true,
      });
      setMap(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!map || !google || countyCounts.size === 0) {return;}

    setGeoJsonError(null);

    const renderLayer = (geoJson) => {
      if (dataLayerRef.current) {
        dataLayerRef.current.setMap(null);
      }

      const dataLayer = new google.maps.Data();
      const bounds = new google.maps.LatLngBounds();

      geoJson.features.forEach((feature) => {
        const p = feature.properties;
        const key = countyKey(p.state, p.NAME);
        const count = countyCounts.get(key) || 0;
        if (count === 0) {return;}

        const max = maxCount || 1;
        const ratio = count > 0 ? logRatio(count, max) : 0;

        const clone = JSON.parse(JSON.stringify(feature));
        clone.properties.count = count;
        clone.properties.ratio = ratio;

        const added = dataLayer.addGeoJson(clone);
        added.forEach((f) => {
          f.setProperty("count", count);
          f.setProperty("ratio", ratio);
          f.setProperty("ageBands", countyAgeBands.get(key) || {});
        });
      });

      const featureMap = new Map();
      dataLayer.forEach((feature) => {
        feature.getGeometry().forEachLatLng((latLng) => bounds.extend(latLng));
        const state = feature.getProperty("state") || "";
        const name = feature.getProperty("NAME") || "";
        featureMap.set(countyKey(state, name), feature);
      });
      featureMapRef.current = featureMap;

      dataLayer.setStyle((feature) => {
        const count = feature.getProperty("count") || 0;
        const ratio = feature.getProperty("ratio") || 0;
        const color = count > 0 ? heatColor(ratio) : "transparent";
        const state = feature.getProperty("state") || "";
        const name = feature.getProperty("NAME") || "";
        const key = countyKey(state, name);
        const isNewArea = newAreaKeys.has(key);
        const isHovered = hoveredKey === key;

        let strokeColor = "#1f2937";
        let strokeWeight = 1.5;
        if (isNewArea && !isHovered) {
          strokeColor = "#7c3aed"; // violet for new areas
        } else if (count > 0 && count < 10 && !isHovered) {
          strokeColor = "#dc2626"; // red for low-count counties
        }

        if (isHovered) {
          strokeColor = "#2563eb"; // blue highlight
          strokeWeight = 4;
        }

        return {
          fillColor: isHovered ? "#93c5fd" : color,
          fillOpacity: count > 0 ? isHovered ? 0.75 : 0.55 : 0,
          strokeColor,
          strokeWeight,
        };
      });

      const infoWindow = new google.maps.InfoWindow();
      dataLayer.addListener("click", (event) => {
        const state = event.feature.getProperty("state") || "";
        const name = event.feature.getProperty("NAME") || "";
        const count = event.feature.getProperty("count") || 0;
        const bands = event.feature.getProperty("ageBands") || {};
        const bandLabels = ["0-7", "8-14", "15-21", "22-30", "31+"];
        const bandRows = bandLabels.map((b) => {
          const n = bands[b] || 0;
          const cls = n === 0 ? "tw-text-gray-400" : "";
          return `<div class="${cls}">${b} days: ${formatNumber(n)}</div>`;
        }).join("");
        const totalOpen = bandLabels.reduce((sum, b) => sum + (bands[b] || 0), 0);
        infoWindow.setContent(
          `<div class="tw-px-2 tw-py-1">` +
            `<div class="tw-font-bold tw-text-base">${formatNumber(count)} inspection${count === 1 ? "" : "s"} this month</div>` +
            `<div class="tw-text-gray-700">${name}, ${state}</div>` +
            (totalOpen > 0 ?
              `<div class="tw-mt-1 tw-pt-1 tw-border-t tw-border-gray-200">` +
                `<div class="tw-text-sm tw-text-gray-500 tw-mb-0.5">Open by age (${formatNumber(totalOpen)})</div>` +
                `<div class="tw-text-sm">${bandRows}</div>` +
              `</div>` : "") +
          `</div>`
        );
        infoWindow.setPosition(event.latLng);
        infoWindow.open(map);
      });

      dataLayer.setMap(map);
      dataLayerRef.current = dataLayer;

      if (!bounds.isEmpty()) {
        const sw = bounds.getSouthWest().toUrlValue(6);
        const ne = bounds.getNorthEast().toUrlValue(6);
        const signature = `${sw}|${ne}`;
        if (signature !== lastFitSignatureRef.current) {
          lastFitSignatureRef.current = signature;
          map.fitBounds(bounds);
        }
      }
    };

    if (allGeoJsonRef.current) {
      renderLayer(allGeoJsonRef.current);
      return;
    }

    const geoJsonUrl = `${config.staticPath}/geo/counties.geojson`;
    fetch(geoJsonUrl)
      .then((res) => {
        if (!res.ok) {throw new Error(`Failed to load counties GeoJSON: ${res.status}`);}
        return res.json();
      })
      .then((geoJson) => {
        allGeoJsonRef.current = geoJson;
        renderLayer(geoJson);
      })
      .catch((err) => {
        console.error("[IncomingMap] Error loading counties GeoJSON", err);
        setGeoJsonError(err.message);
      });
  }, [map, google, countyCounts, maxCount, newAreaKeys, hoveredKey, countyAgeBands]);

  useEffect(() => {
    if (!map || !google) {return;}

    // Clear existing coverage polygons and markers
    coveragePolygonsRef.current.forEach((item) => item.overlay.setMap(null));
    coveragePolygonsRef.current = [];

    const coverageInfoWindow = new google.maps.InfoWindow();
    const inspectors = coverageData?.data?.result?.inspectors || [];
    const color = "#2563eb";

    inspectors.forEach((inspector) => {
      const path = (inspector.polygon || []).map(([lat, lng]) => ({ lat, lng }));
      if (path.length < 3) {return;}

      const polygon = new google.maps.Polygon({
        paths: path,
        fillColor: color,
        fillOpacity: 0.25,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2.5,
        strokeDashArray: [6, 6],
        map,
      });

      const marker = new google.maps.Marker({
        position: { lat: inspector.lat, lng: inspector.lng },
        map,
        title: `${inspector.name} (${inspector.minutes} min reach)`,
        clickable: true,
        zIndex: 1000,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: color,
          fillOpacity: 1.0,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
      });

      polygon.addListener("click", () => {
        coverageInfoWindow.setContent(
          `<div class="tw-px-2 tw-py-1">` +
            `<div class="tw-font-bold tw-text-base">${inspector.name}</div>` +
            `<div class="tw-text-gray-700">${inspector.minutes}-minute driving reach</div>` +
          `</div>`
        );
        coverageInfoWindow.setPosition({ lat: inspector.lat, lng: inspector.lng });
        coverageInfoWindow.open(map);
      });

      coveragePolygonsRef.current.push({ inspectorId: inspector.id, overlay: polygon });
      coveragePolygonsRef.current.push({ inspectorId: inspector.id, overlay: marker });
    });

    return () => {
      coveragePolygonsRef.current.forEach((item) => item.overlay.setMap(null));
      coveragePolygonsRef.current = [];
    };
  }, [map, google, coverageData, coverageMinutes, showCoverage]);



  const displayNameMap = useMemo(() => {
    const map = new Map();
    Object.entries(canonicalNames).forEach(([key, name]) => {
      if (key && name) {
        map.set(key, name);
      }
    });
    return map;
  }, [canonicalNames]);

  const treeData = useMemo(() => {
    const byState = {};

    const ensureCounty = (state, rawCounty) => {
      const key = `${state}|${rawCounty}`.toLowerCase().replace(/[^a-z0-9]/g, "");
      const displayCounty = displayNameMap.get(key) || rawCounty;
      if (!byState[state]) {
        byState[state] = { state, count: 0, lastMonth: 0, lastYear: 0, counties: {} };
      }
      if (!byState[state].counties[key]) {
        byState[state].counties[key] = { county: displayCounty, key, count: 0, lastMonth: 0, lastYear: 0 };
      }
      return byState[state].counties[key];
    };

    currentData.forEach((item) => {
      if (!item.state || !item.county) {return;}
      const c = ensureCounty(item.state, item.county);
      c.count += item.count;
      byState[item.state].count += item.count;
    });

    lastMonthData.forEach((item) => {
      if (!item.state || !item.county) {return;}
      const state = byState[item.state];
      if (!state) {return;}
      const key = `${item.state}|${item.county}`.toLowerCase().replace(/[^a-z0-9]/g, "");
      const c = state.counties[key];
      if (!c) {return;}
      c.lastMonth += item.count;
      state.lastMonth += item.count;
    });

    lastYearData.forEach((item) => {
      if (!item.state || !item.county) {return;}
      const state = byState[item.state];
      if (!state) {return;}
      const key = `${item.state}|${item.county}`.toLowerCase().replace(/[^a-z0-9]/g, "");
      const c = state.counties[key];
      if (!c) {return;}
      c.lastYear += item.count;
      state.lastYear += item.count;
    });

    return Object.values(byState)
      .sort((a, b) => b.count - a.count)
      .map((s) => ({ ...s, counties: Object.values(s.counties).sort((a, b) => {
        if (b.count !== a.count) {return b.count - a.count;}
        return a.county.localeCompare(b.county);
      }) }));
  }, [currentData, lastMonthData, lastYearData, displayNameMap]);

  return (
    <div className="tw-mb-10">
      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-4 tw-mb-4">
        <div>
          <h4 className="tw-text-xl tw-font-bold">Incoming Inspections</h4>
          <p className="tw-text-sm tw-text-gray-600">Newly created inspections by county</p>
        </div>

        <div className="tw-flex tw-items-center tw-gap-4">
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer">
            <input
              type="checkbox"
              className="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-600"
              checked={showCoverage}
              onChange={(e) => setShowCoverage(e.target.checked)}
            />
            <span>Inspector coverage</span>
          </label>
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <span className="tw-font-medium">Reach:</span>
            <select
              className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
              value={coverageMinutes}
              onChange={(e) => setCoverageMinutes(parseInt(e.target.value, 10))}
              disabled={!showCoverage}
            >
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>60 min</option>
              <option value={90}>90 min</option>
              <option value={120}>120 min</option>
            </select>
            {showCoverage && coverageLoading &&
              <CircularProgress size={16} thickness={5} className="tw-ml-2" />
            }
          </label>
        </div>

      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
        <div className="lg:tw-col-span-2">
          <div
            ref={mapDivRef}
            className="tw-w-full tw-rounded-lg tw-shadow-lg tw-overflow-hidden tw-border tw-border-gray-200"
            style={{ height: "900px" }}
          />
          {(loading || !google) &&
            <div
              className="tw-flex tw-items-center tw-justify-center tw-relative tw-z-10 tw-bg-white tw-bg-opacity-80"
              style={{ height: "900px", marginTop: "-900px" }}
            >
              <CircularProgress />
            </div>
          }
          {geoJsonError &&
            <div className="tw-mt-2 tw-text-red-600 tw-text-sm">Map error: {geoJsonError}</div>
          }
        </div>

        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-shadow tw-p-4 tw-flex tw-flex-col" style={{ height: "900px" }}>
          <h5 className="tw-text-lg tw-font-semibold tw-mb-3">Incoming by State & County</h5>
          {treeData.length === 0 ?
            <p className="tw-text-gray-500 tw-text-sm">No data for the selected range.</p>
            :
            <div className="tw-overflow-y-auto tw-flex-1">
              <table className="tw-w-full tw-text-sm">
                <thead className="tw-sticky tw-top-0 tw-bg-white">
                  <tr className="tw-border-b tw-border-gray-200">
                    <th className="tw-py-2 tw-text-left tw-font-semibold">State / County</th>
                    <th className="tw-py-2 tw-text-right tw-font-semibold">{currentLabel}</th>
                    <th className="tw-py-2 tw-text-right tw-font-semibold">{lastMonthLabel}</th>
                    <th className="tw-py-2 tw-text-right tw-font-semibold">{lastYearLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {treeData.map((state) =>
                    <React.Fragment key={`state-${state.state}`}>
                      <tr
                        className="tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-cursor-pointer hover:tw-bg-blue-100"
                        onMouseEnter={() => setHoveredKey(`state-${state.state}`)}
                        onMouseLeave={() => setHoveredKey(null)}
                      >
                        <td className="tw-py-2 tw-font-bold">{state.state}</td>
                        <td className="tw-py-2 tw-text-right tw-font-semibold">
                          {formatNumber(state.count)}
                          <TrendBadge label={lastMonthLabel} current={state.count} previous={state.lastMonth} />
                          <TrendBadge label={lastYearLabel} current={state.count} previous={state.lastYear} />
                        </td>
                        <td className="tw-py-2 tw-text-right tw-text-gray-600">{formatNumber(state.lastMonth)}</td>
                        <td className="tw-py-2 tw-text-right tw-text-gray-600">{formatNumber(state.lastYear)}</td>
                      </tr>
                      {state.counties.map((county) =>
                        <tr
                          key={`county-${state.state}-${county.county}`}
                          className="tw-border-b tw-border-gray-100 tw-cursor-pointer hover:tw-bg-blue-50"
                          onMouseEnter={() => setHoveredKey(county.key)}
                          onMouseLeave={() => setHoveredKey(null)}
                        >
                          <td className="tw-py-2 tw-pl-6">{county.county}</td>
                          <td className="tw-py-2 tw-text-right tw-font-semibold">
                            {formatNumber(county.count)}
                            <TrendBadge label={lastMonthLabel} current={county.count} previous={county.lastMonth} />
                            <TrendBadge label={lastYearLabel} current={county.count} previous={county.lastYear} />
                          </td>
                          <td className="tw-py-2 tw-text-right tw-text-gray-600">{formatNumber(county.lastMonth)}</td>
                          <td className="tw-py-2 tw-text-right tw-text-gray-600">{formatNumber(county.lastYear)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  )}
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>

    </div>
  );
};

export default IncomingInspectionsMap;
